import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { API_ENDPOINT } from '../config';
import { useAuth0 } from '@auth0/auth0-react';

export interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'cal';
  timestamp: Date;
  attachedDocument?: {
    fileName: string;
    documentId: string;
    s3Key: string;
    status?: 'uploading' | 'uploaded' | 'analyzing' | 'analyzed';
  };
}

interface UploadedDocument {
  documentId: string;
  fileName: string;
  s3Key: string;
  uploadTime: Date;
  status: 'uploading' | 'uploaded' | 'analyzing' | 'analyzed';
  gapAnalysis?: any;
}

/**
 * What the user is currently looking at, published by the screen they are on and attached to every
 * message Cal receives. Without it, a question asked while viewing August gets answered against
 * whichever period the model happens to pick.
 */
export interface CalViewContext {
  massBalance?: {
    dataset?: string;   // e.g. "EET"
    period?: string;    // e.g. "2026-08"
    view?: string;      // "Refinery Mass Balance" | "Diagnostic"
  };
}

interface CalChatContextType {
  messages: ChatMessage[];
  sessionId: string;
  viewContext: CalViewContext;
  isTyping: boolean;
  uploadedDocuments: UploadedDocument[];
  unreadCount: number;
  isDrawerOpen: boolean;
  activeView: 'none' | 'floating' | 'compliance' | 'meter' | 'checklist'; // Track which UI is active

  // Actions
  sendMessage: (text: string, attachedDocumentId?: string) => Promise<void>;
  uploadDocument: (file: File) => Promise<string>;
  analyzeDocument: (documentId: string) => Promise<void>;
  clearUnreadCount: () => void;
  toggleDrawer: () => void;
  setActiveView: (view: 'none' | 'floating' | 'compliance' | 'meter' | 'checklist') => void;
  addMessage: (message: Omit<ChatMessage, 'id'> & { id?: string }) => void;
  updateMessage: (id: string, text: string) => void;
  clearMessages: () => void;
  /** Screens call this when what the user is viewing changes; pass {} to clear on unmount. */
  setViewContext: (ctx: CalViewContext) => void;
}

const CalChatContext = createContext<CalChatContextType | undefined>(undefined);

export const useCalChat = () => {
  const context = useContext(CalChatContext);
  if (!context) {
    throw new Error('useCalChat must be used within CalChatProvider');
  }
  return context;
};

export const CalChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, getAccessTokenSilently } = useAuth0();

  // Generate or retrieve session ID - use sessionStorage for per-tab persistence
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('cal-unified-session-id');
    if (!sessionId) {
      sessionId = `cal-unified-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('cal-unified-session-id', sessionId);
    }
    return sessionId;
  };

  const [sessionId] = useState(getSessionId());
  const [viewContext, setViewContextState] = useState<CalViewContext>({});
  // Held in a ref as well so sendMessage reads the CURRENT view without needing to be
  // re-created every time the user changes month — which would re-subscribe the socket.
  const viewContextRef = useRef<CalViewContext>({});
  const setViewContext = useCallback((ctx: CalViewContext) => {
    viewContextRef.current = ctx || {};
    setViewContextState(ctx || {});
  }, []);
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const saved = sessionStorage.getItem('cal-unified-messages');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Convert timestamp strings back to Date objects
        return parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      } catch (e) {
        console.error('Failed to parse saved messages:', e);
        return [];
      }
    }
    return [];
  });
  const [isTyping, setIsTyping] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedDocument[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [activeView, setActiveView] = useState<'none' | 'floating' | 'compliance' | 'meter' | 'checklist'>('none');

  // Save messages to sessionStorage whenever they change
  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('cal-unified-messages', JSON.stringify(messages));
    }
  }, [messages]);

  const sendMessage = useCallback(async (text: string, attachedDocumentId?: string) => {
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      text,
      sender: 'user',
      timestamp: new Date(),
      attachedDocument: attachedDocumentId ?
        uploadedDocuments.find(d => d.documentId === attachedDocumentId) :
        undefined
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    try {
      const token = await getAccessTokenSilently();

      // Build the message with document context if attached
      let enhancedMessage = text;
      if (attachedDocumentId) {
        enhancedMessage = `[Document attached: ${attachedDocumentId}] ${text}`;
      }

      const response = await fetch(`${API_ENDPOINT}/cal/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: enhancedMessage,
          sessionId,
          context: {
            userName: user?.name,
            userEmail: user?.email,
            attachedDocumentId,
            // What the user is looking at right now. Read from the ref so a month change is
            // picked up without re-creating sendMessage. See CalViewContext.
            ...viewContextRef.current
          }
        })
      });

      const data = await response.json();

      const calMessage: ChatMessage = {
        id: `msg-${Date.now()}-cal`,
        text: data.response || 'I apologize, but I encountered an error processing your request.',
        sender: 'cal',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, calMessage]);

      // Only increment unread count if user is not actively viewing any Cal interface
      if (activeView === 'none' && !isDrawerOpen) {
        setUnreadCount(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error sending message:', error);

      const errorMessage: ChatMessage = {
        id: `msg-${Date.now()}-error`,
        text: 'I apologize, but I encountered an error. Please try again.',
        sender: 'cal',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  }, [sessionId, user, getAccessTokenSilently, isDrawerOpen, uploadedDocuments]);

  const uploadDocument = useCallback(async (file: File): Promise<string> => {
    try {
      const token = await getAccessTokenSilently();

      // Get presigned URL
      const urlResponse = await fetch(`${API_ENDPOINT}/cal/upload-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          fileName: file.name,
          fileType: file.type
        })
      });

      const { uploadUrl, fields, documentId } = await urlResponse.json();

      // Track document
      const newDoc: UploadedDocument = {
        documentId,
        fileName: file.name,
        s3Key: documentId,
        uploadTime: new Date(),
        status: 'uploading'
      };

      setUploadedDocuments(prev => [...prev, newDoc]);

      // Upload to S3
      const formData = new FormData();
      Object.keys(fields).forEach(key => {
        formData.append(key, fields[key]);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      // Update status
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'uploaded' }
          : d
        )
      );

      return documentId;
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }, [sessionId, getAccessTokenSilently]);

  const analyzeDocument = useCallback(async (documentId: string) => {
    try {
      const token = await getAccessTokenSilently();

      // Update status to analyzing
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'analyzing' }
          : d
        )
      );

      const response = await fetch(`${API_ENDPOINT}/cal/analyze-gaps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sessionId,
          documentId
        })
      });

      const analysis = await response.json();

      // Update with analysis results
      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'analyzed', gapAnalysis: analysis }
          : d
        )
      );

      // Add a message about the analysis
      const analysisMessage: ChatMessage = {
        id: `msg-${Date.now()}-analysis`,
        text: `Gap analysis complete for ${uploadedDocuments.find(d => d.documentId === documentId)?.fileName}.
               Found ${analysis.gaps?.summary?.critical_count || 0} critical gaps,
               ${analysis.gaps?.summary?.major_count || 0} major gaps, and
               ${analysis.gaps?.summary?.minor_count || 0} minor gaps.`,
        sender: 'cal',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, analysisMessage]);

    } catch (error) {
      console.error('Error analyzing document:', error);

      setUploadedDocuments(prev =>
        prev.map(d => d.documentId === documentId
          ? { ...d, status: 'uploaded' }
          : d
        )
      );
    }
  }, [sessionId, getAccessTokenSilently, uploadedDocuments]);

  const clearUnreadCount = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen(prev => {
      const newState = !prev;
      if (newState) {
        clearUnreadCount();
        setActiveView('floating');
      } else {
        setActiveView('none');
      }
      return newState;
    });
  }, [clearUnreadCount]);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id'> & { id?: string }) => {
    const newMessage: ChatMessage = {
      ...message,
      id: message.id || `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    };
    setMessages(prev => [...prev, newMessage]);

    // Only increment unread if not actively viewing Cal
    if (newMessage.sender === 'cal' && activeView === 'none' && !isDrawerOpen) {
      setUnreadCount(prev => prev + 1);
    }
  }, [activeView, isDrawerOpen]);

  const updateMessage = useCallback((id: string, text: string) => {
    setMessages(prev => prev.map(msg =>
      msg.id === id ? { ...msg, text } : msg
    ));
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    sessionStorage.removeItem('cal-unified-messages');
  }, []);

  const value: CalChatContextType = {
    messages,
    sessionId,
    viewContext,
    setViewContext,
    isTyping,
    uploadedDocuments,
    unreadCount,
    isDrawerOpen,
    activeView,
    sendMessage,
    uploadDocument,
    analyzeDocument,
    clearUnreadCount,
    toggleDrawer,
    setActiveView,
    addMessage,
    updateMessage,
    clearMessages
  };

  return (
    <CalChatContext.Provider value={value}>
      {children}
    </CalChatContext.Provider>
  );
};